import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { taskAction } from 'actions';
import DeleteModal from 'components/Modal/DeleteModal';
import EditModal from 'components/Modal/EditModal';
import { RouterPromptMobile } from 'components/RouterPromptMobile';
import { TEMPORARY } from 'helpers/constant';
import { timeFormat } from 'helpers/date';
import useMobile from 'hooks/useMobile';
import { MoreHorizontal, NotebookText, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Task } from 'types/reducers/task';

interface TaskListProps {
  onAddNew: () => void;
  onChange: (_task: Task) => void;
  onCloseSidebarMobile: () => void;
}

const TaskListMobile = ({ onAddNew, onChange, onCloseSidebarMobile }: TaskListProps) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { tasks, current } = useSelector((state) => state.task);
  const { data, total_page, current_page } = tasks;
  const { isMobile } = useMobile();

  const [recordDelete, setRecordDelete] = useState<Task>();
  const [recordEdit, setRecordEdit] = useState<Task>();
  const [selectTask, setSelectTask] = useState<Task>();

  const {
    isOpen: isOpenEditDialog,
    onOpen: onOpenEditDialog,
    onClose: onCloseEditDialog,
  } = useDisclosure();
  const {
    isOpen: isOpenDeleteDialog,
    onOpen: onOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
  } = useDisclosure();

  const selectedTaskId = current?.task_id;
  const isChangingSelectedFiles =
    JSON.stringify(current?.selectedFiles) !== JSON.stringify(current?.initialSelectedFiles);

  const onShowMore = () => {
    dispatch(
      taskAction.getTasks({
        page: current_page + 1,
        size: 10,
      })
    );
  };

  const onClickDelete = (task: Task) => {
    setRecordDelete(task);
    onOpenDeleteDialog();
  };

  const onClickEdit = (task: Task) => {
    setRecordEdit(task);
    onOpenEditDialog();
  };

  const handleDelete = (task: Task) => {
    dispatch(
      taskAction.deleteTask({
        id: task.id,
      })
    );
  };

  const handleEdit = (updatedTask: Task & { new_name: string }) => {
    dispatch(
      taskAction.editTaskName({
        id: updatedTask.id,
        new_name: updatedTask.new_name,
      })
    );
  };

  const onChangeTask = (task: Task) => {
    if (isChangingSelectedFiles && current.type !== TEMPORARY && current.task_id !== task.id) {
      setSelectTask(task);
    } else {
      onContinueChangeTask(task);
    }
  };

  const onContinueChangeTask = (task: Task) => {
    onChange(task);
  };

  useEffect(() => {
    if (current?.task_id) {
      dispatch(taskAction.getDetailTask(current));
    }
  }, [current?.task_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {isMobile && <RouterPromptMobile when={isChangingSelectedFiles} />}

      <DeleteModal
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        recordDelete={recordDelete}
        action={handleDelete}
        dataHeader={t('summary.deleteTask')}
        dataBody={t('summary.deleteTaskInstruction')}
        btnAction={t('common.delete')}
        color='red'
      />
      <EditModal
        isOpen={isOpenEditDialog}
        onClose={onCloseEditDialog}
        recordEdit={recordEdit}
        action={handleEdit}
        dataHeader={t('summary.renameTask')}
        btnAction={t('summary.rename')}
      />

      <Box>
        <Flex align='center' justify='space-between' mb={3}>
          <Text color='text.default' fontFamily='heading' fontSize='md' fontWeight={600}>
            {t('summary.tasks')}
          </Text>
          <Tooltip label={t('common.addNew')}>
            <IconButton
              aria-label={t('common.addNew')}
              icon={<Plus size={17} />}
              isDisabled={data?.[0]?.type === TEMPORARY}
              onClick={onAddNew}
              size='ds-md'
              variant='ds-soft'
            />
          </Tooltip>
        </Flex>

        <Text
          px={2}
          pb={1}
          color='text.subtle'
          fontSize='xs'
          fontWeight={600}
          letterSpacing='0.04em'
          textTransform='uppercase'
        >
          {t('common.recent')}
        </Text>

        <Flex direction='column' gap='2px'>
          {data?.map((item) => {
            const isActive = selectedTaskId === item.id;
            return (
              <Flex
                key={item.id}
                align='flex-start'
                gap={3}
                px={3}
                py={3}
                bg={isActive ? 'primary.soft' : 'transparent'}
                borderRadius='md'
                _hover={{ bg: isActive ? 'primary.soft' : 'bg.canvas' }}
                cursor='pointer'
                transition='background .12s ease-out'
                onClick={() => {
                  onChangeTask(item);
                  onCloseSidebarMobile();
                }}
              >
                <Box flexShrink={0} mt='2px' color={isActive ? 'primary.solid' : 'text.subtle'}>
                  <NotebookText size={16} />
                </Box>
                <Box flex={1} minW={0}>
                  <Tooltip label={item.name} placement='top-start'>
                    <Text
                      overflow='hidden'
                      color={isActive ? 'primary.solid' : 'text.default'}
                      fontSize='sm'
                      fontWeight={isActive ? 600 : 500}
                      whiteSpace='nowrap'
                      textOverflow='ellipsis'
                    >
                      {item.name}
                    </Text>
                  </Tooltip>
                  <Text mt='2px' color='text.subtle' fontSize='xs'>
                    {timeFormat(item?.last_updated)}
                  </Text>
                </Box>
                <TaskOptions data={item} onClickDelete={onClickDelete} onClickEdit={onClickEdit} />
              </Flex>
            );
          })}
        </Flex>

        {current_page + 1 < total_page && (
          <Box
            color='primary.solid'
            fontSize='xs'
            textAlign='right'
            textDecoration='underline'
            cursor='pointer'
            onClick={onShowMore}
          >
            {t('common.moreHistory')}
          </Box>
        )}
      </Box>
    </>
  );
};

interface TaskOptionsProps {
  data: Task;
  onClickDelete: (_task: Task) => void;
  onClickEdit: (_task: Task) => void;
}

const TaskOptions = ({ data, onClickDelete, onClickEdit }: TaskOptionsProps) => {
  const { t } = useTranslation();
  return (
    <Menu placement='bottom-end'>
      <MenuButton
        as={IconButton}
        flexShrink={0}
        aria-label={t('common.edit')}
        icon={<MoreHorizontal size={16} />}
        onClick={(e) => e.stopPropagation()}
        size='ds-sm'
        variant='ds-ghost'
      />
      <MenuList minW='140px' bg='bg.surfaceRaised' borderColor='border.default' shadow='ds_lg'>
        <MenuItem
          color='text.default'
          fontSize='sm'
          bg='transparent'
          _hover={{ bg: 'primary.soft', color: 'primary.solid' }}
          onClick={(e) => {
            e.stopPropagation();
            onClickEdit(data);
          }}
        >
          {t('common.edit')}
        </MenuItem>
        <MenuItem
          color='state.error'
          fontSize='sm'
          bg='transparent'
          _hover={{ bg: 'state.errorSoft' }}
          onClick={(e) => {
            e.stopPropagation();
            onClickDelete(data);
          }}
        >
          {t('common.delete')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default TaskListMobile;
